// Demo dataset — gives a brand-new tenant a populated app to explore.
//
// Seeds a small but coherent BBQ-joint dataset:
//   - 6 menu items (sandwiches, ribs, sides) with prices
//   - 7 inventory items (brisket, pork, ribs, buns, etc.) with costs
//   - 6 recipes with components so menu engineering can compute CM
//   - 14 days of item_sales with day-of-week weighting (weekend peaks)
//
// Everything is suffixed " (demo)" so it's trivially identifiable
// and removable. Clear button finds rows by name suffix and deletes
// them in FK-safe order.
//
// We don't tag with a hidden flag because changing the schema for
// a demo feature is overkill. The suffix approach keeps the schema
// clean and lets the manager see it's demo content while exploring.

import { supabase } from './supabase.js'

const SUFFIX = ' (demo)'

// Inventory ingredients with cost, par, and on-hand baseline.
//
// Pars are tuned so two items (Cabbage, Cheddar) fall below par after
// the seeded receivings + sales + waste play out — gives Reorder
// something to flag.
//
// Baselines are tuned so the Variance screen lands at roughly 1-6%
// per item (realistic shrinkage signal) instead of -99% across the
// board. The math: variance = baseline + recv − waste − now − theoretical.
// Numbers were back-computed from theoretical usage in the 7-day
// window so the demo tells a believable "small over- and
// under-portioning across items" story.
const INVENTORY = [
  { name: 'Brisket',         unit: 'lb',   cost: 9.50,  pack_size: 14, pack_unit: 'case', par: 30,  baseline: 109 },
  { name: 'Pork Shoulder',   unit: 'lb',   cost: 4.20,  pack_size: 8,  pack_unit: 'case', par: 20,  baseline: 82  },
  { name: 'Pork Ribs',       unit: 'lb',   cost: 7.00,  pack_size: 6,  pack_unit: 'case', par: 12,  baseline: 165 },
  { name: 'Sandwich Bun',    unit: 'each', cost: 0.45,  pack_size: 24, pack_unit: 'case', par: 100, baseline: 420 },
  { name: 'Cabbage',         unit: 'lb',   cost: 1.20,  pack_size: 1,  pack_unit: 'each', par: 10,  baseline: 49  },
  { name: 'Macaroni',        unit: 'lb',   cost: 2.20,  pack_size: 20, pack_unit: 'case', par: 15,  baseline: 47  },
  { name: 'Cheddar Cheese',  unit: 'lb',   cost: 5.50,  pack_size: 5,  pack_unit: 'case', par: 8,   baseline: 18  },
]

// Sellable menu items with sale_price. Each links to a recipe below.
const MENU = [
  {
    name: 'Brisket Sandwich',
    sale_price: 14.95,
    recipe: [
      { ingredient: 'Brisket',      qty: 0.45, unit: 'lb' },   // ~$4.28
      { ingredient: 'Sandwich Bun', qty: 1,    unit: 'each' }, // $0.45
    ],
  },
  {
    name: 'Pulled Pork Sandwich',
    sale_price: 12.95,
    recipe: [
      { ingredient: 'Pork Shoulder', qty: 0.40, unit: 'lb' },   // ~$1.68
      { ingredient: 'Sandwich Bun',  qty: 1,    unit: 'each' }, // $0.45
    ],
  },
  {
    name: 'Half Rack Ribs',
    sale_price: 18.95,
    recipe: [
      { ingredient: 'Pork Ribs', qty: 1.2, unit: 'lb' }, // ~$8.40
    ],
  },
  {
    name: 'Full Rack Ribs',
    sale_price: 32.95,
    recipe: [
      { ingredient: 'Pork Ribs', qty: 2.4, unit: 'lb' }, // ~$16.80
    ],
  },
  {
    name: 'Mac & Cheese',
    sale_price: 5.95,
    recipe: [
      { ingredient: 'Macaroni',       qty: 0.25, unit: 'lb' }, // $0.55
      { ingredient: 'Cheddar Cheese', qty: 0.15, unit: 'lb' }, // $0.83
    ],
  },
  {
    name: 'Coleslaw',
    sale_price: 3.95,
    recipe: [
      { ingredient: 'Cabbage', qty: 0.5, unit: 'lb' }, // $0.60
    ],
  },
]

// Day-of-week sales weights. Saturday peak, Tuesday trough.
// Indexed Sun=0, Mon=1, ..., Sat=6 to match JS Date.getDay().
const DOW_WEIGHTS = [0.95, 0.55, 0.50, 0.65, 0.80, 1.10, 1.30]

// Per-item base daily volume (what a "neutral" day sells). Brisket
// sandwich is the hit; coleslaw rides as a side; full rack ribs sell
// less often than half because of price. These create a realistic
// menu-engineering matrix.
const DAILY_BASE_VOLUME = {
  'Brisket Sandwich':      35, // popular + good margin → Star
  'Pulled Pork Sandwich':  28, // popular + great margin → Star
  'Half Rack Ribs':        12, // moderate + thin margin → Plowhorse-ish
  'Full Rack Ribs':         5, // slow + great margin → Puzzle
  'Mac & Cheese':          22, // popular + great margin → Star
  'Coleslaw':              18, // popular + great margin → Star
}

// Suppliers — two distributors a small BBQ joint would use.
const SUPPLIERS = [
  { name: 'Sysco', contact: 'Mike at Sysco · 555-0142', notes: 'Proteins + dry goods. Tue/Fri delivery.' },
  { name: 'Restaurant Depot', contact: 'pickup, no rep', notes: 'Cash & carry for buns, paper goods, last-minute fills.' },
]

// Staff — small crew typical for a single-location BBQ spot.
const STAFF = [
  { name: 'Marcus Harris',   role: 'pitmaster',  hourly_wage: 22.00 },
  { name: 'Jaylen Brown',    role: 'line cook',  hourly_wage: 17.50 },
  { name: 'Ana Reyes',       role: 'server',     hourly_wage: 8.00 },  // tipped
  { name: 'Tyler Webb',      role: 'server',     hourly_wage: 8.00 },  // tipped
  { name: 'Sam Patel',       role: 'dishwasher', hourly_wage: 14.00 },
]

// Compliance documents — every restaurant needs these. We seed them
// all valid (expirations 3-12 months out) so the docs tracker shows
// healthy state, but with one approaching renewal so the warning
// surface has something to render.
function complianceRows() {
  const today = new Date()
  function plusDays(n) {
    const d = new Date(today); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10)
  }
  return [
    { doc_type: 'liquor_license',      name: 'Liquor License — Class C',  holder_name: 'I Smell Bacon LLC',
      expiration_date: plusDays(45),  // approaching, will warn
      notes: 'Up for renewal soon — file by ' + plusDays(20) },
    { doc_type: 'food_handler_cert',   name: "ServSafe — Pitmaster",      holder_name: 'Marcus Harris',
      expiration_date: plusDays(280), notes: null },
    { doc_type: 'business_license',    name: 'City Business License',     holder_name: 'I Smell Bacon LLC',
      expiration_date: plusDays(190), notes: null },
    { doc_type: 'health_permit',       name: 'County Health Permit',      holder_name: 'I Smell Bacon LLC',
      expiration_date: plusDays(120), notes: 'Last inspection: 92/100' },
    { doc_type: 'insurance',           name: 'General Liability Policy',  holder_name: 'I Smell Bacon LLC',
      expiration_date: plusDays(310), notes: 'Carrier: State Farm' },
  ]
}

// Promotions — one running, one upcoming, one in past for history feel.
function promotionsRows() {
  const today = new Date()
  function plusDays(n) {
    const d = new Date(today); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10)
  }
  return [
    {
      name: 'Memorial Day BBQ Weekend',
      description: '$2 off Half Racks all weekend. Family pack pricing for parties of 4+.',
      starts_on: plusDays(20), ends_on: plusDays(22),
      channels: ['instagram', 'facebook'],
      cost_estimate: 0, status: 'planned',
      style_notes: 'Texas BBQ vibe — keep it casual, no corporate-speak',
    },
    {
      name: 'Tuesday Sandwich Special',
      description: 'Brisket sandwich + side + drink combo for $14.95 (saves $4).',
      starts_on: plusDays(-30), ends_on: null,
      channels: ['instagram', 'sms'],
      cost_estimate: 0, status: 'live',
      style_notes: 'Driver of slow weekday traffic',
    },
    {
      name: 'Mother\'s Day Brunch',
      description: 'Brunch service 10am-2pm, BBQ bloody marys, kids eat free with adult entrée.',
      starts_on: plusDays(-60), ends_on: plusDays(-60),
      channels: ['instagram', 'facebook'],
      cost_estimate: 50, status: 'completed',
      style_notes: null,
    },
  ]
}

export async function isDemoLoaded(restaurantId) {
  const { count, error } = await supabase
    .from('items')
    .select('id', { count: 'exact', head: true })
    .eq('restaurant_id', restaurantId)
    .like('name', `%${SUFFIX}`)
  if (error) {
    // eslint-disable-next-line no-console
    console.error('[demoData] isDemoLoaded check failed', error)
    return false
  }
  return (count || 0) > 0
}

// Seed demo data. Idempotent-ish: if demo data already exists, no-op.
export async function loadDemoData(restaurantId) {
  if (!restaurantId) throw new Error('restaurantId required')

  if (await isDemoLoaded(restaurantId)) {
    return { ok: true, alreadyLoaded: true }
  }

  // 1. Insert all items (inventory + menu) in one shot.
  const allItemRows = [
    ...INVENTORY.map((inv) => ({
      restaurant_id: restaurantId,
      name: inv.name + SUFFIX,
      unit: inv.unit,
      cost_per_unit: inv.cost,
      pack_size: inv.pack_size,
      pack_unit: inv.pack_unit,
      sale_price: null,
      par_qty: inv.par,
      active: true,
    })),
    ...MENU.map((m) => ({
      restaurant_id: restaurantId,
      name: m.name + SUFFIX,
      unit: 'each',
      cost_per_unit: 0,            // menu items don't track stock cost the same way
      pack_size: 1,                // NOT NULL on items; sensible default for sold-by-the-each menu items
      sale_price: m.sale_price,
      par_qty: 0,                  // NOT NULL on items; menu items don't have par stock
      active: true,
    })),
  ]

  const { data: insertedItems, error: itemErr } = await supabase
    .from('items')
    .insert(allItemRows)
    .select('id, name')
  if (itemErr) throw itemErr

  // Build a lookup by canonical (un-suffixed) name.
  const itemIdByName = {}
  for (const it of insertedItems) {
    const canonical = it.name.replace(SUFFIX, '')
    itemIdByName[canonical] = it.id
  }

  // 2. Insert recipes for each menu item.
  const recipeRows = MENU.map((m) => ({
    restaurant_id: restaurantId,
    item_id: itemIdByName[m.name],
    name: m.name + SUFFIX,
    yield_servings: 1,
    target_food_cost_pct: 30,
  }))

  const { data: insertedRecipes, error: recErr } = await supabase
    .from('recipes')
    .insert(recipeRows)
    .select('id, item_id')
  if (recErr) throw recErr

  // Map menu item id → recipe id.
  const recipeIdByItemId = {}
  for (const r of insertedRecipes) {
    recipeIdByItemId[r.item_id] = r.id
  }

  // 3. Insert recipe_components.
  const componentRows = []
  for (const m of MENU) {
    const menuItemId = itemIdByName[m.name]
    const recipeId   = recipeIdByItemId[menuItemId]
    for (const comp of m.recipe) {
      const compItemId = itemIdByName[comp.ingredient]
      if (!compItemId) continue
      componentRows.push({
        restaurant_id: restaurantId,
        recipe_id:     recipeId,
        item_id:       compItemId,
        quantity:      comp.qty,
        unit:          comp.unit,
      })
    }
  }
  if (componentRows.length > 0) {
    const { error: compErr } = await supabase.from('recipe_components').insert(componentRows)
    if (compErr) throw compErr
  }

  // 4. Insert 14 days of item_sales — last 14 days, ending yesterday.
  // We use period_start = period_end = a single date so the data
  // shows up cleanly in any date-range query.
  const sales = []
  const today = new Date()
  for (let dayOffset = 14; dayOffset >= 1; dayOffset--) {
    const d = new Date(today)
    d.setDate(d.getDate() - dayOffset)
    const dow = d.getDay()
    const dateStr = d.toISOString().slice(0, 10)
    const weight = DOW_WEIGHTS[dow]

    for (const m of MENU) {
      const menuItemId = itemIdByName[m.name]
      const baseVol    = DAILY_BASE_VOLUME[m.name] || 10
      const noise      = 0.85 + Math.random() * 0.3 // ±15%
      const qty        = Math.round(baseVol * weight * noise)
      if (qty <= 0) continue
      const grossSales = qty * m.sale_price
      const netSales   = grossSales * 0.92 // ~8% mix of comps/discounts
      sales.push({
        restaurant_id:   restaurantId,
        item_id:         menuItemId,
        item_name:       m.name + SUFFIX,
        period_start:    dateStr,
        period_end:      dateStr,
        quantity_sold:   qty,
        gross_sales:     +grossSales.toFixed(2),
        net_sales:       +netSales.toFixed(2),
        source:          'demo',
      })
    }
  }
  if (sales.length > 0) {
    const { error: salesErr } = await supabase.from('item_sales').insert(sales)
    if (salesErr) throw salesErr
  }

  // 4b. daily_sales rollup. The Daily Huddle reads "today's sales"
  // from daily_sales (POS-imported daily totals), NOT item_sales
  // (which is per-item per-day for menu engineering / variance).
  // Without this rollup, the Huddle shows $0 for today even though
  // item_sales has data. Roll it up here so all screens come alive.
  const dailyByDate = new Map()
  for (const s of sales) {
    const key = s.period_start
    const cur = dailyByDate.get(key) || { gross: 0, net: 0, qty: 0 }
    cur.gross += Number(s.gross_sales)
    cur.net   += Number(s.net_sales)
    cur.qty   += Number(s.quantity_sold)
    dailyByDate.set(key, cur)
  }
  // Also seed a row for TODAY so the Huddle's "Today's Sales" tile
  // populates immediately on load. We tune today's volume so the
  // labor % vs sales ratio comes out healthy (~30%) — otherwise the
  // brief screams about a "labor disaster" because today seeds
  // partial-day shifts but full DOW-weighted sales is too high.
  // Multiplier 1.5x of normal Tuesday volume = ~$1500 in sales,
  // matched against today's reduced shift list (~$425 labor) for a
  // ~28% labor ratio.
  {
    const todayDow = today.getDay()
    const todayWeight = DOW_WEIGHTS[todayDow]
    const todayKey = today.toISOString().slice(0, 10)
    let todayGross = 0
    for (const m of MENU) {
      const baseVol = DAILY_BASE_VOLUME[m.name] || 10
      const partial = baseVol * todayWeight * 1.5
      todayGross += partial * m.sale_price
    }
    const todayQty = Math.round(todayGross / 18)
    dailyByDate.set(todayKey, {
      gross: todayGross,
      net:   todayGross * 0.92,
      qty:   todayQty,
    })
  }
  const dailyRows = Array.from(dailyByDate.entries()).map(([dateStr, agg]) => ({
    restaurant_id:     restaurantId,
    sale_date:         dateStr,
    gross_sales:       +agg.gross.toFixed(2),
    net_sales:         +agg.net.toFixed(2),
    transaction_count: agg.qty, // proxy: items sold ≈ tickets for BBQ
    source:            'demo',
  }))
  const todayKey = today.toISOString().slice(0, 10)
  const historicalRows = dailyRows.filter((r) => r.sale_date !== todayKey)
  const todayRow = dailyRows.find((r) => r.sale_date === todayKey)
  if (historicalRows.length > 0) {
    // Real POS data may already populate historical dates; ignoreDuplicates keeps real history untouched.
    const { error: dsErr } = await supabase
      .from('daily_sales')
      .upsert(historicalRows, { onConflict: 'restaurant_id,sale_date', ignoreDuplicates: true })
    if (dsErr) throw dsErr
  }
  if (todayRow) {
    // Today's row deliberately overwrites — the 1.5x volume tune above keeps the Huddle labor ratio healthy
    // even if a partial-day POS sync has already landed today.
    const { error: dsErr } = await supabase
      .from('daily_sales')
      .upsert([todayRow], { onConflict: 'restaurant_id,sale_date', ignoreDuplicates: false })
    if (dsErr) throw dsErr
  }

  // 5. Suppliers — needed before receive_logs reference them.
  const supplierRows = SUPPLIERS.map((s) => ({
    restaurant_id: restaurantId,
    name: s.name + SUFFIX,
    contact: s.contact,
    notes: s.notes,
    active: true,
  }))
  const { data: insertedSuppliers, error: supErr } = await supabase
    .from('suppliers').insert(supplierRows).select('id, name')
  if (supErr) throw supErr
  const supplierIdByName = {}
  for (const s of insertedSuppliers) {
    supplierIdByName[s.name.replace(SUFFIX, '')] = s.id
  }

  // 6. Staff — needed before schedule_assignments reference them.
  const staffRows = STAFF.map((p) => ({
    restaurant_id: restaurantId,
    name: p.name + SUFFIX,
    role: p.role,
    hourly_wage: p.hourly_wage,
    active: true,
  }))
  const { data: insertedStaff, error: staffErr } = await supabase
    .from('staff').insert(staffRows).select('id, name, role, hourly_wage')
  if (staffErr) throw staffErr

  // 7. Schedule assignments — last 14 days through next 7 days.
  // Range covers two weeks so the Insights "this week vs prior week"
  // labor comparison has data on both sides. Skip Mondays (closed).
  // For TODAY only, we deliberately omit the late-shift staff (Tyler
  // who comes in at 15:30, Sam at 16:00) — they haven't clocked in
  // yet, so logging their full shift hours as "today's labor cost"
  // makes the labor % vs sales ratio look terrible in the brief.
  const shiftRows = []
  for (let dayOffset = -14; dayOffset <= 7; dayOffset++) {
    const d = new Date(today)
    d.setDate(d.getDate() + dayOffset)
    const dow = d.getDay()
    if (dow === 1) continue // closed Mondays
    const dateStr = d.toISOString().slice(0, 10)
    const isToday = dayOffset === 0
    for (const member of insertedStaff) {
      // For today, skip the late-shift staff who haven't started yet.
      if (isToday) {
        if (member.role === 'dishwasher') continue
        if (member.name.includes('Tyler')) continue
      }
      // Stagger times by role.
      let start = '09:00', end = '17:00'
      if (member.role === 'pitmaster')   { start = '06:00'; end = '14:00' }
      if (member.role === 'line cook')   { start = '09:00'; end = '20:00' }
      if (member.role === 'server') {
        if (member.name.includes('Ana'))   { start = '10:30'; end = '17:30' }
        if (member.name.includes('Tyler')) { start = '15:30'; end = '21:30' }
      }
      if (member.role === 'dishwasher')  { start = '16:00'; end = '21:00' }
      shiftRows.push({
        restaurant_id: restaurantId,
        staff_id: member.id,
        shift_date: dateStr,
        start_time: start,
        end_time: end,
        role: member.role,
        status: 'scheduled',
      })
    }
  }
  if (shiftRows.length > 0) {
    const { error: shiftErr } = await supabase.from('schedule_assignments').insert(shiftRows)
    if (shiftErr) throw shiftErr
  }

  // 8. Receive logs — 5 deliveries over the last 14 days, mostly proteins.
  // Includes a TODAY delivery so the Receive screen has something on it
  // when the manager opens the app, plus a brisket price uptick mid-run
  // for the anomaly scanner to flag.
  const receiveLogs = []
  const receiveSchedule = [
    { daysAgo: 13, supplier: 'Sysco',                items: [
      { name: 'Brisket',       qty: 28, unit: 'lb',   cost: 9.50 },
      { name: 'Pork Shoulder', qty: 20, unit: 'lb',   cost: 4.20 },
      { name: 'Pork Ribs',     qty: 18, unit: 'lb',   cost: 7.00 },
    ]},
    { daysAgo: 9,  supplier: 'Restaurant Depot',     items: [
      { name: 'Sandwich Bun',  qty: 96, unit: 'each', cost: 0.45 },
      { name: 'Macaroni',      qty: 20, unit: 'lb',   cost: 2.20 },
    ]},
    { daysAgo: 5,  supplier: 'Sysco',                items: [
      { name: 'Brisket',       qty: 28, unit: 'lb',   cost: 9.65 }, // small price uptick — anomaly fodder
      { name: 'Pork Shoulder', qty: 16, unit: 'lb',   cost: 4.20 },
      { name: 'Cheddar Cheese',qty: 5,  unit: 'lb',   cost: 5.50 },
    ]},
    { daysAgo: 2,  supplier: 'Restaurant Depot',     items: [
      { name: 'Cabbage',       qty: 8,  unit: 'lb',   cost: 1.20 },
      { name: 'Sandwich Bun',  qty: 48, unit: 'each', cost: 0.45 },
    ]},
    { daysAgo: 0,  supplier: 'Sysco',                items: [
      { name: 'Brisket',       qty: 24, unit: 'lb',   cost: 9.65 },
      { name: 'Pork Ribs',     qty: 12, unit: 'lb',   cost: 7.00 },
    ]},
  ]
  for (const delivery of receiveSchedule) {
    const d = new Date(today); d.setDate(d.getDate() - delivery.daysAgo)
    const loggedAt = new Date(d); loggedAt.setHours(11, 0, 0, 0)
    for (const li of delivery.items) {
      const itemId = itemIdByName[li.name]
      if (!itemId) continue
      receiveLogs.push({
        restaurant_id: restaurantId,
        supplier_id: supplierIdByName[delivery.supplier] || null,
        supplier_name: delivery.supplier + SUFFIX,
        item_id: itemId,
        item_name: li.name + SUFFIX,
        quantity: li.qty,
        unit: li.unit,
        cost_per_unit_at_log: li.cost,
        total_cost: +(li.qty * li.cost).toFixed(2),
        logged_at: loggedAt.toISOString(),
        notes: 'Demo receive — auto-seeded.',
      })
    }
  }
  if (receiveLogs.length > 0) {
    const { error: rcvErr } = await supabase.from('receive_logs').insert(receiveLogs)
    if (rcvErr) throw rcvErr
  }

  // 9. Waste logs — entries spread across both this week and prior
  // week so the Insights week-over-week waste comparison has data
  // on both sides. Also includes one TODAY entry so the Waste tab
  // shows recent-entry content immediately.
  const wasteEvents = [
    { daysAgo: 13, item: 'Pork Shoulder', qty: 0.6, reason: 'overcooked', notes: 'Pulled apart too soon' },
    { daysAgo: 11, item: 'Sandwich Bun',  qty: 8,   reason: 'expired',    notes: 'Forgot bag in walk-in' },
    { daysAgo: 10, item: 'Coleslaw',      qty: 1.2, reason: 'expired',    notes: null },
    { daysAgo: 8,  item: 'Brisket',       qty: 0.5, reason: 'dropped',    notes: 'Slipped off cutting board' },
    { daysAgo: 6,  item: 'Brisket',       qty: 0.8, reason: 'overcooked', notes: 'Pulled too late, dried out' },
    { daysAgo: 5,  item: 'Coleslaw',      qty: 1.5, reason: 'expired',    notes: "End-of-day prep, didn't move" },
    { daysAgo: 4,  item: 'Pork Ribs',     qty: 0.5, reason: 'dropped',    notes: null },
    { daysAgo: 3,  item: 'Sandwich Bun',  qty: 12,  reason: 'expired',    notes: 'Stale — found at close' },
    { daysAgo: 2,  item: 'Mac & Cheese',  qty: 0.3, reason: 'returned',   notes: 'Customer ordered wrong side' },
    { daysAgo: 1,  item: 'Brisket',       qty: 0.4, reason: 'overcooked', notes: null },
    { daysAgo: 0,  item: 'Coleslaw',      qty: 0.4, reason: 'expired',    notes: 'Morning prep — small amount' },
  ]
  const wasteRows = []
  for (const w of wasteEvents) {
    const itemId = itemIdByName[w.item]
    if (!itemId) continue
    const d = new Date(today); d.setDate(d.getDate() - w.daysAgo)
    d.setHours(20, 0, 0, 0)
    // Find unit + cost from inventory definition.
    const inv = INVENTORY.find((i) => i.name === w.item)
    const unit = inv?.unit || 'each'
    const cost = inv?.cost || 1
    const menu = MENU.find((m) => m.name === w.item)
    const finalUnit = menu ? 'each' : unit
    const finalCost = menu ? (menu.sale_price * 0.3) : cost  // assume 30% COGS for menu items
    wasteRows.push({
      restaurant_id: restaurantId,
      item_id: itemId,
      item_name: w.item + SUFFIX,
      quantity: w.qty,
      unit: finalUnit,
      cost_per_unit_at_log: finalCost,
      total_cost: +(w.qty * finalCost).toFixed(2),
      reason: w.reason,
      notes: w.notes,
      logged_at: d.toISOString(),
    })
  }
  if (wasteRows.length > 0) {
    const { error: wErr } = await supabase.from('waste_logs').insert(wasteRows)
    if (wErr) throw wErr
  }

  // 10. Compliance documents.
  const docRows = complianceRows().map((d) => ({
    restaurant_id: restaurantId,
    doc_type:        d.doc_type,
    name:            d.name + SUFFIX,
    holder_name:     d.holder_name,
    expiration_date: d.expiration_date,
    notes:           d.notes,
    archived:        false,
  }))
  if (docRows.length > 0) {
    const { error: dErr } = await supabase.from('compliance_documents').insert(docRows)
    if (dErr) throw dErr
  }

  // 10b. On-hand baseline — a starting snapshot 14 days ago for each
  // inventory item. Without this, the Variance and Reorder screens
  // come up empty ("No inventory baseline yet"), because both
  // compute against an initial on-hand quantity. We seed a generous
  // starting count so reorder suggestions don't immediately fire.
  const baselineDate = new Date(today)
  baselineDate.setDate(baselineDate.getDate() - 14)
  baselineDate.setHours(0, 0, 0, 0)
  const baselineRows = INVENTORY.map((inv) => ({
    restaurant_id: restaurantId,
    item_id: itemIdByName[inv.name],
    snapshot_at: baselineDate.toISOString(),
    quantity: inv.baseline, // tuned so variance lands at 1-6% per item
    source: 'baseline',
    notes: 'Demo seed — initial on-hand baseline.',
  })).filter((r) => r.item_id) // skip any items that didn't get an id
  if (baselineRows.length > 0) {
    const { error: bErr } = await supabase.from('on_hand_snapshots').insert(baselineRows)
    if (bErr) throw bErr
  }

  // 11. Promotions — past, current, upcoming.
  const promoRows = promotionsRows().map((p) => ({
    restaurant_id: restaurantId,
    name:          p.name + SUFFIX,
    description:   p.description,
    starts_on:     p.starts_on,
    ends_on:       p.ends_on,
    channels:      p.channels,
    cost_estimate: p.cost_estimate,
    status:        p.status,
    style_notes:   p.style_notes,
  }))
  if (promoRows.length > 0) {
    const { error: pErr } = await supabase.from('promotions').insert(promoRows)
    if (pErr) throw pErr
  }

  return {
    ok: true,
    items:        insertedItems.length,
    sales:        sales.length,
    daily_sales:  dailyRows.length,
    suppliers:    insertedSuppliers.length,
    staff:        insertedStaff.length,
    shifts:       shiftRows.length,
    receivings:   receiveLogs.length,
    waste:        wasteRows.length,
    baseline:     baselineRows.length,
    compliance:   docRows.length,
    promotions:   promoRows.length,
  }
}

// Tear-down: delete all demo data in FK-safe order. Wipes everything
// suffixed " (demo)" across items, recipes, suppliers, staff, schedule,
// receive logs, waste logs, compliance docs, promotions.
export async function clearDemoData(restaurantId) {
  if (!restaurantId) throw new Error('restaurantId required')

  // Find demo IDs across the tables that have a SUFFIX'd name.
  const [
    itemsRes, recipesRes, suppliersRes, staffRes, complianceRes, promoRes,
  ] = await Promise.all([
    supabase.from('items').select('id').eq('restaurant_id', restaurantId).like('name', `%${SUFFIX}`),
    supabase.from('recipes').select('id, item_id').eq('restaurant_id', restaurantId).like('name', `%${SUFFIX}`),
    supabase.from('suppliers').select('id').eq('restaurant_id', restaurantId).like('name', `%${SUFFIX}`),
    supabase.from('staff').select('id').eq('restaurant_id', restaurantId).like('name', `%${SUFFIX}`),
    supabase.from('compliance_documents').select('id').eq('restaurant_id', restaurantId).like('name', `%${SUFFIX}`),
    supabase.from('promotions').select('id').eq('restaurant_id', restaurantId).like('name', `%${SUFFIX}`),
  ])
  if (itemsRes.error) throw itemsRes.error

  const itemIds      = (itemsRes.data       || []).map((r) => r.id)
  const recipeIds    = (recipesRes.data     || []).map((r) => r.id)
  const supplierIds  = (suppliersRes.data   || []).map((r) => r.id)
  const staffIds     = (staffRes.data       || []).map((r) => r.id)
  const docIds       = (complianceRes.data  || []).map((r) => r.id)
  const promoIds     = (promoRes.data       || []).map((r) => r.id)

  if (itemIds.length === 0 && supplierIds.length === 0 && staffIds.length === 0
      && docIds.length === 0 && promoIds.length === 0) {
    return { ok: true, removed: 0 }
  }

  // Delete in FK-safe order. Each group runs in parallel where it can.
  // Schedule assignments depend on staff. Receive/waste depend on items.
  if (staffIds.length > 0) {
    await supabase.from('schedule_assignments').delete().in('staff_id', staffIds)
  }
  if (itemIds.length > 0) {
    await Promise.all([
      supabase.from('item_sales').delete().in('item_id', itemIds).eq('restaurant_id', restaurantId),
      supabase.from('receive_logs').delete().in('item_id', itemIds).eq('restaurant_id', restaurantId),
      supabase.from('waste_logs').delete().in('item_id', itemIds).eq('restaurant_id', restaurantId),
      supabase.from('on_hand_snapshots').delete().in('item_id', itemIds).eq('restaurant_id', restaurantId),
    ])
  }
  // Belt-and-suspenders: also wipe any receive_logs / waste_logs whose
  // item_id was nulled out by a prior cascade (FK SET NULL) or that
  // were created against demo items in a previous load. Match by name
  // suffix on either item_name or supplier_name.
  //
  // NOTE: previously this used a single `.or('item_name.like.%X,supplier_name.like.%X')`
  // call but PostgREST's or-filter parser breaks on the parentheses + space
  // inside the SUFFIX value " (demo)" — the query silently no-ops, the
  // toast says "Cleared", but the rows persist. Two separate deletes
  // are unambiguous and always work. Found 2026-05-06 when Scott saw
  // 12 leftover receive_logs after a successful-looking clear.
  await Promise.all([
    supabase.from('receive_logs').delete()
      .eq('restaurant_id', restaurantId)
      .like('item_name', `%${SUFFIX}`),
    supabase.from('receive_logs').delete()
      .eq('restaurant_id', restaurantId)
      .like('supplier_name', `%${SUFFIX}`),
    supabase.from('waste_logs').delete()
      .eq('restaurant_id', restaurantId)
      .like('item_name', `%${SUFFIX}`),
  ])
  // Purchase orders created during a demo (manager tapped "Send Order"
  // on a reorder suggestion). The demo seed itself doesn't insert any
  // purchase_orders, but the user can generate them while exploring.
  // After the demo items get deleted, the PO header survives because
  // nothing else sweeps it — the row shows up in "Recent orders" with
  // supplier_id null ("Unassigned"). Two cleanup signals:
  //   1. Any PO whose lines reference a demo item_id.
  //   2. Any PO whose lines reference an item_name with the demo SUFFIX
  //      (covers the case where item_id was already SET NULL by a
  //      previous partial cleanup).
  //   3. Belt-and-suspenders: any PO whose supplier_name carries the
  //      demo SUFFIX (covers PO headers created against a demo supplier
  //      before the supplier row was deleted).
  // purchase_order_items.po_id is `on delete cascade`, so deleting the
  // parent purchase_orders header takes the line items with it.
  {
    const poIdSet = new Set()
    if (itemIds.length > 0) {
      const { data: byId } = await supabase
        .from('purchase_order_items')
        .select('po_id')
        .in('item_id', itemIds)
      for (const r of byId || []) poIdSet.add(r.po_id)
    }
    const { data: byName } = await supabase
      .from('purchase_order_items')
      .select('po_id')
      .like('item_name', `%${SUFFIX}`)
    for (const r of byName || []) poIdSet.add(r.po_id)
    const poIdsToDelete = Array.from(poIdSet)
    if (poIdsToDelete.length > 0) {
      await supabase.from('purchase_orders').delete()
        .eq('restaurant_id', restaurantId)
        .in('id', poIdsToDelete)
    }
    await supabase.from('purchase_orders').delete()
      .eq('restaurant_id', restaurantId)
      .like('supplier_name', `%${SUFFIX}`)
  }
  // daily_sales rows seeded by the demo carry source='demo' so we can
  // wipe them without touching real POS-imported daily_sales rows.
  await supabase.from('daily_sales').delete()
    .eq('restaurant_id', restaurantId).eq('source', 'demo')
  if (recipeIds.length > 0) {
    await supabase.from('recipe_components').delete().in('recipe_id', recipeIds)
  }
  if (itemIds.length > 0) {
    await supabase.from('recipe_components').delete().in('item_id', itemIds).eq('restaurant_id', restaurantId)
  }
  if (recipeIds.length > 0) {
    await supabase.from('recipes').delete().in('id', recipeIds)
  }
  if (itemIds.length > 0) {
    await supabase.from('items').delete().in('id', itemIds)
  }
  if (supplierIds.length > 0) {
    await supabase.from('suppliers').delete().in('id', supplierIds)
  }
  if (staffIds.length > 0) {
    await supabase.from('staff').delete().in('id', staffIds)
  }
  if (docIds.length > 0) {
    await supabase.from('compliance_documents').delete().in('id', docIds)
  }
  if (promoIds.length > 0) {
    await supabase.from('promotions').delete().in('id', promoIds)
  }

  return {
    ok: true,
    removed: itemIds.length + supplierIds.length + staffIds.length + docIds.length + promoIds.length,
  }
}
